import { queryOptions, useMutation } from '@tanstack/react-query'

import {
  ContactRequest as APIContactRequest,
  ContactResponse,
  LegalEntityResponseV3 as APILegalEntity,
  OrganizationRequest,
  OrganizationResponse,
  PagedOrganizationResponse as APIOrganizationResponse,
  VatResponse as APIVatResponse,
} from '@forto-dev/customer-master-data-models'

import { ApiConfig, clientFactory, EndpointConfig } from '@/api/clientFactory'

import { Contacts, ContactsSchema } from '../domain/contact'
import { LegalEntities, LegalEntitiesSchema, RequestLegalEntitySchemaType } from '../domain/legalEntity'
import { Organization, Organizations, OrganizationSchema, OrganizationsSchema } from '../domain/organization'
import { VatValidationData, VatValidationDataSchema } from '../domain/vat'

const basePath = '/api/customer-master-data'
const shippingBasePath = '/api/shipping'

export const ORGANIZATIONS_QUERY_KEY = 'organizations'
export const CONTACTS_QUERY_KEY = 'contacts'

export type PaginationResponse<T> = {
  items: T[]
  total: number
}

type LegalEntityRequest = RequestLegalEntitySchemaType & {
  customerId: string
  organizationId: string
  billingEntityId: string
  hasVat: boolean
}

export const masterDataConfig: ApiConfig<{
  getOrganizations: EndpointConfig<string, APIOrganizationResponse, Organizations>
  updateOrganization: EndpointConfig<Organization, void>
  createOrganization: EndpointConfig<OrganizationRequest, OrganizationResponse, Organization>
  deleteOrganization: EndpointConfig<{ id: string; customerId: string }, void>
  getLegalEntities: EndpointConfig<string, PaginationResponse<APILegalEntity>, LegalEntities>
  requestLegalEntity: EndpointConfig<LegalEntityRequest, void>
  validateVAT: EndpointConfig<string, APIVatResponse, VatValidationData>
  getContacts: EndpointConfig<string[], ContactResponse[], Contacts>
  createContact: EndpointConfig<APIContactRequest, void>
  updateContact: EndpointConfig<{ id: string; contactRequest: APIContactRequest }, void>
  deleteContact: EndpointConfig<{ id: string; customerId: string }, void>
}> = {
  getOrganizations: {
    requestConfig: (customerID) => ({
      endpoint: `${basePath}/customer-core/organizations?customerId=${customerID}`,
      method: 'get',
      deserializationStrategy: OrganizationsSchema,
    }),
  },
  updateOrganization: {
    requestConfig: (organization) => ({
      endpoint: `${basePath}/customer-core/organizations/${organization.id}?customerId=${organization.customerId}`,
      method: 'put',
      body: organization,
    }),
  },
  createOrganization: {
    requestConfig: (organization) => ({
      endpoint: `${basePath}/customer-core/organizations`,
      method: 'post',
      body: organization,
      deserializationStrategy: OrganizationSchema,
    }),
  },
  deleteOrganization: {
    requestConfig: ({ id, customerId }) => ({
      endpoint: `${basePath}/customer-core/organizations/${id}?customerId=${customerId}`,
      method: 'delete',
    }),
  },
  getLegalEntities: {
    requestConfig: (organizationId) => ({
      endpoint: `${basePath}/v3/legal-entities?organizationId=${organizationId}`,
      method: 'get',
      deserializationStrategy: LegalEntitiesSchema,
    }),
  },
  requestLegalEntity: {
    requestConfig: (legalEntityRequest: LegalEntityRequest) => ({
      endpoint: `${shippingBasePath}/legalEntities`,
      method: 'post',
      body: legalEntityRequest,
    }),
  },
  validateVAT: {
    requestConfig: (vatNumber) => ({
      endpoint: `${basePath}/vat/validation?vatID=${vatNumber}`,
      method: 'get',
      deserializationStrategy: VatValidationDataSchema,
    }),
  },
  getContacts: {
    requestConfig: (contactIds) => ({
      endpoint: `${basePath}/customer-core/contacts`,
      method: 'get',
      deserializationStrategy: ContactsSchema,
      options: {
        params: { ids: contactIds.join(',') },
      },
    }),
  },
  createContact: {
    requestConfig: (contactRequest) => ({
      endpoint: `${basePath}/customer-core/contacts`,
      method: 'post',
      body: contactRequest,
    }),
  },
  updateContact: {
    requestConfig: ({ id, contactRequest }) => ({
      endpoint: `${basePath}/customer-core/contacts/${id}?customerId=${contactRequest.customerId}`,
      method: 'put',
      body: contactRequest,
    }),
  },
  deleteContact: {
    requestConfig: ({ id, customerId }) => ({
      endpoint: `${basePath}/customer-core/contacts/${id}?customerId=${customerId}`,
      method: 'delete',
    }),
  },
}

const masterDataClient = clientFactory(masterDataConfig)

export const organizationsQueryOptions = (customerID?: string) => {
  return queryOptions({
    queryKey: [ORGANIZATIONS_QUERY_KEY, customerID],
    queryFn: () => masterDataClient.getOrganizations(customerID || ''),
    enabled: !!customerID,
  })
}

export const useUpdateOrganization = () => {
  return useMutation({
    mutationFn: masterDataClient.updateOrganization,
  })
}

export const useCreateOrganization = () => {
  return useMutation({
    mutationFn: masterDataClient.createOrganization,
  })
}

export const useDeleteOrganization = () => {
  return useMutation({
    mutationFn: masterDataClient.deleteOrganization,
  })
}

export const useValidateVAT = () => {
  return useMutation({
    mutationFn: masterDataClient.validateVAT,
  })
}

export const legalEntitiesQueryOptions = (organizationId: string) => {
  return queryOptions({
    queryKey: ['legalEntities', organizationId],
    queryFn: () => masterDataClient.getLegalEntities(organizationId),
    enabled: !!organizationId,
  })
}

export const useRequestLegalEntity = () => {
  return useMutation({
    mutationFn: masterDataClient.requestLegalEntity,
  })
}

export const contactsQueryOptions = (contactIds: string[]) => {
  return queryOptions({
    queryKey: [CONTACTS_QUERY_KEY, contactIds],
    queryFn: () => masterDataClient.getContacts(contactIds),
  })
}

export const useCreateContact = () => {
  return useMutation({
    mutationFn: masterDataClient.createContact,
  })
}

export const useUpdateContact = () => {
  return useMutation({
    mutationFn: masterDataClient.updateContact,
  })
}

export const useDeleteContact = () => {
  return useMutation({
    mutationFn: masterDataClient.deleteContact,
  })
}
